/**
 * The Supplement page's Review list: which parsed lines still need a code
 * (B-437b), plus the lines the export summary flagged (B-7202). Split out of
 * V2SupplementPage (god-file ratchet).
 */
import type { ParsedEstimateResponse, ParsedLineItem, Provenance } from '../../types';

/** A line item that didn't map to a catalog code (empty CAT), with the room +
 *  index coordinates the override endpoint needs. B-437b review list. */
export interface UnmappedRef {
  roomIdx: number;
  lineItemIndex: number;
  roomLabel: string;
  li: ParsedLineItem;
}

/** A line the backend couldn't map to a catalog code. The parser stamps these
 *  `provenance = UNMAPPED` with the SENTINEL code "UNK UNKNOWN"
 *  (final_draft_parser.convert_to_parsed_estimate) — the category is "UNK", NOT
 *  empty — and match_stats.unmatched counts exactly these. Testing `!li.category`
 *  MISSED them (it's "UNK"), so the review list said "all mapped" while the
 *  Unmapped stat said 1. Key off provenance so the two always agree. */
export function isUnmappedLine(li: ParsedLineItem): boolean {
  return li.provenance === ('UNMAPPED' as Provenance) || li.category === 'UNK' || !li.category;
}

/** Collect every unmapped line item across all rooms, in order, with the
 *  (roomIdx, lineItemIndex) coordinates `overrideLineItem` is keyed on. */
export function collectUnmapped(res: ParsedEstimateResponse, flagged?: Set<number>): UnmappedRef[] {
  const out: UnmappedRef[] = [];
  res.estimate.rooms.forEach((room, roomIdx) => {
    const roomLabel = room.group_description || room.group_code || `Room ${roomIdx + 1}`;
    room.line_items.forEach((li, lineItemIndex) => {
      // B-7202: "Go to review" from the export dialog also lists the lines the
      // export summary flagged (weak matches, custom lines), until the user
      // picks a code for one (provenance MANUAL), which is what resolves it.
      const flaggedHere =
        !!flagged?.has(li.line_number) && li.provenance !== ('MANUAL' as Provenance);
      if (isUnmappedLine(li) || flaggedHere) out.push({ roomIdx, lineItemIndex, roomLabel, li });
    });
  });
  return out;
}
